import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import Text from "@components/Text";
import Button from "@components/Button";
import Input from "@components/Input";
import Spinner from "@components/Spinner";
import { getUsers, updateUserEmailVerified, AdminUser } from "@services/adminService";
import { ROUTES } from "@stores/AccountStore";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInputStyled = styled(Input)`
  height: 40px;
  flex: 1;
  max-width: 600px;
  padding: 0 12px;
  box-sizing: border-box;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: none;
  }
`;

const SearchButton = styled(Button)`
  width: auto;
  min-width: 80px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const Badge = styled.span`
  display: inline-block;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
  background: #f0f1f3;
  border-radius: 12px;
  padding: 4px 12px;
  margin-bottom: 16px;
`;

const Table = styled.div`
  width: 100%;
`;

const SubPill = styled.span<{ paid: boolean }>`
  display: inline-block;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  padding: 3px 10px;
  color: ${({ paid }) => (paid ? "#1f7a3d" : "#899093")};
  background: ${({ paid }) => (paid ? "#e6f4ea" : "#f0f1f3")};
`;

// Clickable: toggles email_verified straight from the list. Unconfirmed users
// cannot log in at all (requireEmailVerification), so this is the fast manual
// bypass for accounts whose verification mail never arrived.
// A span rather than a button: the pill sits inside the row's <a>, and
// interactive elements can't be nested in an anchor.
const StatusPill = styled.span<{ verified: boolean }>`
  display: inline-block;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 3px 10px;
  cursor: pointer;
  transition: 0.2s;
  color: ${({ verified }) => (verified ? "#1f7a3d" : "#9a6700")};
  background: ${({ verified }) => (verified ? "#e6f4ea" : "#fff3d6")};
  &:hover {
    border-color: ${({ verified }) => (verified ? "#1f7a3d" : "#9a6700")};
  }
  &[aria-disabled="true"] {
    opacity: 0.6;
    cursor: default;
    border-color: transparent;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterSelect = styled.select`
  height: 40px;
  padding: 0 12px;
  box-sizing: border-box;
  background: #f8f8f8;
  border: 1px solid transparent;
  border-radius: 4px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #494c5b;
  cursor: pointer;
  outline: none;
  transition: 0.4s;
  :hover,
  :focus {
    border: 1px solid #899093;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 40px;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeeef;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const TableRow = styled(Link)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 40px;
  padding: 16px;
  border-bottom: 1px solid #f5f5f5;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  &:hover {
    background: #fafafa;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 12px;
  }
`;

const Arrow = styled.span`
  color: #899093;
  font-size: 18px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    align-self: flex-end;
  }
`;

const MobileCell = styled.div<{ label?: string }>`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    gap: 8px;
    align-items: baseline;
    font-size: 13px;
    &::before {
      content: "${({ label }) => label || ""}";
      font-size: 11px;
      color: #899093;
      min-width: 80px;
      flex-shrink: 0;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  padding-bottom: 32px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-bottom: 24px;
  }
`;

const PageButton = styled(Button)`
  width: auto;
  min-width: 80px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const PAGE_SIZE = 50;

const AdminUsersTab: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [verified, setVerified] = useState<"" | "verified" | "unverified">("");
  const [subscription, setSubscription] = useState<"" | "paid" | "unpaid">("");
  const [devicesFilter, setDevicesFilter] = useState<"" | "with" | "without">("");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingVerifiedId, setSavingVerifiedId] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUsers({
        search: searchQuery || undefined,
        verified: verified || undefined,
        subscription: subscription || undefined,
        devices: devicesFilter || undefined,
        order,
        limit: PAGE_SIZE,
        offset,
      });
      setUsers(data.users);
      setTotal(data.total);
    } catch (e) {
      console.error("Failed to fetch users:", e);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, verified, subscription, devicesFilter, order, offset]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setOffset(0);
    setSearchQuery(search);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  // The pill lives inside the row link, so the click must not navigate. Both
  // directions ask first: a stray click would otherwise silently confirm an
  // address nobody proved they own, or lock a working account out.
  const handleToggleVerified = (
    e: React.MouseEvent | React.KeyboardEvent,
    user: AdminUser
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (savingVerifiedId) return;

    const next = !user.emailVerified;
    const question = next
      ? `Confirm ${user.email} manually? They will be able to log in without the e-mail code.`
      : `Mark ${user.email} as unconfirmed? They will have to verify by e-mail before their next login.`;
    if (!window.confirm(question)) return;

    setSavingVerifiedId(user.id);
    updateUserEmailVerified(user.id, next)
      .then(() =>
        setUsers((prev) =>
          prev.map((u) => (u.id === user.id ? { ...u, emailVerified: next } : u))
        )
      )
      .catch((err) => console.error("Failed to update email status:", err))
      .finally(() => setSavingVerifiedId(null));
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div>
      <SearchRow>
        <SearchInputStyled
          placeholder="Search by name, email, org or user ID..."
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
          onKeyDown={handleKeyDown}
        />
        <SearchButton kind="outline" onClick={handleSearch}>
          Search
        </SearchButton>
      </SearchRow>

      <FilterRow>
        <FilterSelect
          value={verified}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setOffset(0);
            setVerified(e.target.value as "" | "verified" | "unverified");
          }}
        >
          <option value="">All statuses</option>
          <option value="unverified">Unconfirmed only</option>
          <option value="verified">Confirmed only</option>
        </FilterSelect>

        <FilterSelect
          value={subscription}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setOffset(0);
            setSubscription(e.target.value as "" | "paid" | "unpaid");
          }}
        >
          <option value="">All subscriptions</option>
          <option value="paid">Paid only</option>
          <option value="unpaid">Unpaid only</option>
        </FilterSelect>

        <FilterSelect
          value={devicesFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setOffset(0);
            setDevicesFilter(e.target.value as "" | "with" | "without");
          }}
        >
          <option value="">All users</option>
          <option value="with">Has devices</option>
          <option value="without">No devices</option>
        </FilterSelect>

        <FilterSelect
          value={order}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setOffset(0);
            setOrder(e.target.value as "asc" | "desc");
          }}
        >
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </FilterSelect>
      </FilterRow>

      <Badge>{total} users</Badge>

      <Table>
        <TableHeader>
          <Text size={12} color="#899093" type="heading">
            USER
          </Text>
          <Text size={12} color="#899093" type="heading">
            STATUS
          </Text>
          <Text size={12} color="#899093" type="heading">
            SUBSCRIPTION
          </Text>
          <Text size={12} color="#899093" type="heading">
            DEVICES
          </Text>
          <Text size={12} color="#899093" type="heading">
            FIELD BOUNDARIES
          </Text>
          <Text size={12} color="#899093" type="heading">
            CREATED
          </Text>
          <span />
        </TableHeader>

        {loading ? (
          <LoadingWrap>
            <Spinner />
          </LoadingWrap>
        ) : (
          users.map((user) => (
            <TableRow key={user.id} to={`${ROUTES.ADMIN}/users/${user.id}`}>
              <div>
                {user.name && user.name !== user.email && (
                  <Text size={14} color="#181B1F">
                    {user.name}
                  </Text>
                )}
                <Text size={13} color="#899093">
                  {user.email}
                </Text>
              </div>
              <MobileCell label="Status:">
                <StatusPill
                  role="button"
                  tabIndex={0}
                  verified={user.emailVerified}
                  aria-disabled={savingVerifiedId !== null}
                  title={
                    user.emailVerified
                      ? "Click to mark unconfirmed"
                      : "Click to confirm manually (skips the e-mail code)"
                  }
                  onClick={(e: React.MouseEvent) => handleToggleVerified(e, user)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ") handleToggleVerified(e, user);
                  }}
                >
                  {savingVerifiedId === user.id
                    ? "Saving…"
                    : user.emailVerified
                      ? "Confirmed"
                      : "Unconfirmed"}
                </StatusPill>
              </MobileCell>
              <MobileCell label="Subscription:">
                <SubPill paid={user.subscriptionPaid}>
                  {user.subscriptionPaid ? "Paid" : "Free"}
                </SubPill>
              </MobileCell>
              <MobileCell label="Devices:">
                <Text size={13} color="#494C5B">
                  {user.deviceCount}
                </Text>
              </MobileCell>
              <MobileCell label="Field boundaries:">
                <Text size={13} color="#494C5B">
                  {user.fieldBoundaryCount}
                </Text>
              </MobileCell>
              <MobileCell label="Created:">
                <Text size={13} color="#494C5B">
                  {formatDate(user.createdAt)}
                </Text>
              </MobileCell>
              <Arrow>&rarr;</Arrow>
            </TableRow>
          ))
        )}
      </Table>

      {!loading && total > PAGE_SIZE && (
        <Pagination>
          <PageButton
            kind="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </PageButton>
          <Text size={13} color="#899093">
            {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
          </Text>
          <PageButton
            kind="outline"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </PageButton>
        </Pagination>
      )}
    </div>
  );
};

export default AdminUsersTab;
